/* eslint-disable rumio-i18n/no-hardcoded-strings */
import { useCallback, type ReactNode } from 'react';
import { AnimatePresence } from 'framer-motion';
import { SlotThemeProvider } from './context/SlotThemeContext';
import { SlotMachineScene } from './SlotMachineScene';
import { ReelContainer } from './components/Scene/ReelContainer';
import { HoldAndSpinBonus } from './components/UI/HoldAndSpinBonus';
import { RumioJackpotCelebration } from './components/UI/RumioJackpotCelebration';
import { rumioDefaultTheme } from './themes/rumio-default/theme.config';
import { useShouldRender3D } from './hooks/useSlotTheme';
import type { SlotTheme, SymbolId, RenderQuality } from './themes/types';
import type { HoldAndSpinState, HoldAndSpinSpinResult } from './hooks/useSlotMachineV2';

// =============================================================================
// Types
// =============================================================================

export interface SpinResult {
  /** 5x3 grid of symbol IDs */
  grid: SymbolId[][];
  /** Winning combinations */
  wins: Array<{
    lineNumber: number;
    symbolId: SymbolId;
    matchCount: number;
    positions: Array<{ col: number; row: number }>;
    winAmount: number;
  }>;
  /** Total win amount */
  totalWin: number;
  /** Whether free spins were triggered */
  freeSpinsTriggered?: {
    spins: number;
    multiplier: number;
  };
  /** Whether bonus game was triggered */
  bonusTriggered?: boolean;
}

export interface SlotMachine3DProps {
  /** Custom theme (defaults to Rumio theme) */
  theme?: SlotTheme;
  /** Force a specific render quality */
  forceQuality?: RenderQuality;
  /** Whether the spin is in progress */
  isSpinning?: boolean;
  /** Result from the last spin */
  spinResult?: SpinResult | null;
  /** Callback when spin button is clicked */
  onSpin?: () => void;
  /** Callback when all reels have stopped */
  onSpinComplete?: () => void;
  /** Callback when a win is detected */
  onWin?: (result: SpinResult) => void;
  /** Current bet amount per line */
  betPerLine?: number;
  /** Number of active lines */
  activeLines?: number;
  /** Current balance */
  balance?: number;
  /** Enable orbit controls for development */
  enableOrbitControls?: boolean;
  /** Custom CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
  /** Child components (overlays, etc.) */
  children?: ReactNode;

  // Hold & Spin props
  /** Whether Hold & Spin bonus is active */
  isHoldAndSpinActive?: boolean;
  /** Hold & Spin game state */
  holdAndSpinState?: HoldAndSpinState | null;
  /** Callback to execute a Hold & Spin round */
  onHoldAndSpinSpin?: () => Promise<HoldAndSpinSpinResult | null>;
  /** Callback when Hold & Spin completes */
  onHoldAndSpinComplete?: (payout: number, isGrandBonus: boolean) => void;
  /** Callback to close Hold & Spin overlay */
  onHoldAndSpinClose?: () => void;

  // RUMIO Jackpot props
  /** Whether to show jackpot celebration */
  showJackpotCelebration?: boolean;
  /** Jackpot prize info */
  jackpotPrize?: {
    payout: number;
    months: number;
    row: number | null;
  } | null;
  /** Callback when jackpot celebration is dismissed */
  onJackpotClose?: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function SlotMachine3D({
  theme = rumioDefaultTheme,
  forceQuality,
  isSpinning = false,
  spinResult = null,
  onSpin,
  onSpinComplete,
  onWin,
  betPerLine = 1,
  activeLines = 20,
  balance = 0,
  enableOrbitControls = false,
  className = '',
  style,
  children,
  // Hold & Spin props
  isHoldAndSpinActive = false,
  holdAndSpinState = null,
  onHoldAndSpinSpin,
  onHoldAndSpinComplete,
  onHoldAndSpinClose,
  // RUMIO Jackpot props
  showJackpotCelebration = false,
  jackpotPrize = null,
  onJackpotClose,
}: SlotMachine3DProps) {
  return (
    <SlotThemeProvider theme={theme} forceQuality={forceQuality}>
      <SlotMachineInner
        isSpinning={isSpinning}
        spinResult={spinResult}
        onSpin={onSpin}
        onSpinComplete={onSpinComplete}
        onWin={onWin}
        betPerLine={betPerLine}
        activeLines={activeLines}
        balance={balance}
        enableOrbitControls={enableOrbitControls}
        className={className}
        style={style}
        isHoldAndSpinActive={isHoldAndSpinActive}
        holdAndSpinState={holdAndSpinState}
        onHoldAndSpinSpin={onHoldAndSpinSpin}
        onHoldAndSpinComplete={onHoldAndSpinComplete}
        onHoldAndSpinClose={onHoldAndSpinClose}
        showJackpotCelebration={showJackpotCelebration}
        jackpotPrize={jackpotPrize}
        onJackpotClose={onJackpotClose}
      >
        {children}
      </SlotMachineInner>
    </SlotThemeProvider>
  );
}

// =============================================================================
// Inner Component (has access to theme context)
// =============================================================================

type SlotMachineInnerProps = Omit<SlotMachine3DProps, 'theme' | 'forceQuality'>;

function SlotMachineInner({
  isSpinning,
  spinResult,
  onSpin,
  onSpinComplete,
  onWin,
  betPerLine,
  activeLines,
  balance,
  enableOrbitControls,
  className,
  style,
  children,
  // Hold & Spin props
  isHoldAndSpinActive,
  holdAndSpinState,
  onHoldAndSpinSpin,
  onHoldAndSpinComplete,
  onHoldAndSpinClose,
  // RUMIO Jackpot props
  showJackpotCelebration,
  jackpotPrize,
  onJackpotClose,
}: SlotMachineInnerProps) {
  const shouldRender3D = useShouldRender3D();

  // Handle all reels stopped
  const handleAllReelsStopped = useCallback(() => {
    // Check for wins
    if (spinResult && spinResult.totalWin > 0) {
      onWin?.(spinResult);
    }

    onSpinComplete?.();
  }, [spinResult, onWin, onSpinComplete]);

  // Calculate winning positions from spin result
  const winningPositions = spinResult?.wins.flatMap((win) => win.positions) ?? [];

  if (!shouldRender3D) {
    // Render 2D fallback
    return (
      <SlotMachine2DFallback
        isSpinning={isSpinning ?? false}
        spinResult={spinResult ?? null}
        onSpin={onSpin}
        betPerLine={betPerLine ?? 1}
        activeLines={activeLines ?? 20}
        balance={balance ?? 0}
        className={className}
        style={style}
      />
    );
  }

  return (
    <div
      className={`relative ${className}`}
      style={{
        width: '100%',
        height: '500px',
        ...style,
      }}
    >
      {/* 3D Scene */}
      <SlotMachineScene
        enableOrbitControls={enableOrbitControls}
        className="w-full h-full"
      >
        {/* Reel Container */}
        <ReelContainer
          isSpinning={isSpinning ?? false}
          gridResult={spinResult?.grid ?? null}
          winningPositions={winningPositions}
          onAllReelsStopped={handleAllReelsStopped}
        />
      </SlotMachineScene>

      {/* UI Overlay */}
      <SlotMachineUI
        balance={balance ?? 0}
        activeLines={activeLines ?? 20}
        totalBet={(betPerLine ?? 1) * (activeLines ?? 20)}
        lastWin={spinResult?.totalWin ?? 0}
        isSpinning={isSpinning ?? false}
        onSpin={onSpin}
      />

      {/* Custom overlays */}
      {children}

      {/* Hold & Spin Bonus Game Overlay */}
      <AnimatePresence>
        {isHoldAndSpinActive && onHoldAndSpinSpin && onHoldAndSpinComplete && onHoldAndSpinClose && (
          <HoldAndSpinBonus
            isActive={isHoldAndSpinActive}
            initialGrid={holdAndSpinState?.grid}
            initialSpins={holdAndSpinState?.spinsRemaining}
            betPerLine={betPerLine}
            onSpin={onHoldAndSpinSpin}
            onComplete={onHoldAndSpinComplete}
            onClose={onHoldAndSpinClose}
          />
        )}
      </AnimatePresence>

      {/* RUMIO Jackpot Celebration Overlay */}
      <AnimatePresence>
        {showJackpotCelebration && jackpotPrize && onJackpotClose && (
          <RumioJackpotCelebration
            isActive={showJackpotCelebration}
            jackpot={jackpotPrize}
            onClose={onJackpotClose}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// UI Overlay
// =============================================================================

interface SlotMachineUIProps {
  balance: number;
  activeLines: number;
  totalBet: number;
  lastWin: number;
  isSpinning: boolean;
  onSpin?: () => void;
}

function SlotMachineUI({
  balance,
  activeLines,
  totalBet,
  lastWin,
  isSpinning,
  onSpin,
}: SlotMachineUIProps) {
  return (
    <div className="absolute inset-x-0 bottom-0 p-4">
      {/* Bottom control bar */}
      <div className="flex items-center justify-between bg-black/60 backdrop-blur-sm rounded-lg p-3">
        {/* Balance */}
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase">Balance</div>
          <div className="text-lg font-bold text-white">{balance}</div>
        </div>

        {/* Bet Info */}
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase">Total Bet</div>
          <div className="text-lg font-bold text-yellow-400">{totalBet}</div>
        </div>

        {/* Spin Button */}
        <button
          onClick={onSpin}
          disabled={isSpinning || balance < totalBet}
          className={`
            px-8 py-3 rounded-full font-bold text-lg uppercase
            transition-all duration-200 transform
            ${
              isSpinning
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : balance < totalBet
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:scale-105 hover:shadow-lg active:scale-95'
            }
          `}
        >
          {isSpinning ? 'Spinning...' : 'SPIN'}
        </button>

        {/* Last Win */}
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase">Last Win</div>
          <div
            className={`text-lg font-bold ${lastWin > 0 ? 'text-green-400' : 'text-white'}`}
          >
            {lastWin}
          </div>
        </div>

        {/* Lines/Bet */}
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase">Lines</div>
          <div className="text-lg font-bold text-white">{activeLines}</div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 2D Fallback
// =============================================================================

interface SlotMachine2DFallbackProps {
  isSpinning: boolean;
  spinResult: SpinResult | null;
  onSpin?: () => void;
  betPerLine: number;
  activeLines: number;
  balance: number;
  className?: string;
  style?: React.CSSProperties;
}

function SlotMachine2DFallback({
  isSpinning,
  spinResult,
  onSpin,
  betPerLine,
  activeLines,
  balance,
  className,
  style,
}: SlotMachine2DFallbackProps) {
  // Simple 2D fallback using CSS animations
  // This will be expanded in a future iteration
  return (
    <div
      className={`relative bg-gradient-to-b from-slate-900 to-slate-800 rounded-xl p-4 ${className}`}
      style={style}
    >
      <div className="text-center text-white py-20">
        <p className="text-lg mb-4">2D Mode</p>
        <p className="text-sm text-gray-400 mb-6">
          Your device is using the simplified 2D slot machine.
        </p>

        {/* Simplified reel display */}
        <div className="flex justify-center gap-2 mb-6">
          {spinResult?.grid.map((column, colIndex) => (
            <div
              key={colIndex}
              className="bg-slate-700 rounded p-2 w-16 h-24 flex flex-col justify-center items-center"
            >
              {column.map((symbolId, rowIndex) => (
                <div key={rowIndex} className="text-2xl">
                  {getSymbolEmoji(symbolId)}
                </div>
              ))}
            </div>
          )) ??
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`bg-slate-700 rounded p-2 w-16 h-24 flex items-center justify-center ${isSpinning ? 'animate-pulse' : ''}`}
              >
                <span className="text-2xl">?</span>
              </div>
            ))}
        </div>

        <button
          onClick={onSpin}
          disabled={isSpinning || balance < betPerLine * activeLines}
          className="px-6 py-2 bg-yellow-500 text-black font-bold rounded-lg disabled:opacity-50"
        >
          {isSpinning ? 'Spinning...' : 'SPIN'}
        </button>
      </div>
    </div>
  );
}

// Simple emoji mapping for 2D fallback
function getSymbolEmoji(symbolId: SymbolId): string {
  const emojiMap: Record<string, string> = {
    rumio_ring: '💍',
    wild: '⭐',
    scatter: '✨',
    bonus: '🎁',
    sofa: '🛋️',
    lamp: '💡',
    chair: '🪑',
    table: '🪵',
    plant: '🌿',
    letter_r: 'R',
    letter_u: 'U',
    letter_m: 'M',
    letter_i: 'I',
    letter_o: 'O',
  };
  return emojiMap[symbolId] ?? '?';
}

export default SlotMachine3D;
